import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import PickContactEmailModal from "../components/PickContactEmailModal";
import { api } from "../lib/api";
import { ALLE_CONTACTEN_API, contactListApiPath } from "../lib/contactApiPaths";
import {
  contactEmailOptions,
  contactPreferredEmail,
  type Contact,
  type ContactEmailOption,
  type User,
} from "@domaddress/shared";

const STORAGE_KEY = "domaddress-zend-contacten-v1";

export type ZendContactenListItem = {
  contact: Contact;
  sourceApiPath: string;
  /** Gekozen adres voor Aan-veld; leeg als geen e-mail op de kaart. */
  sendEmail: string;
};

export type ZendContactenSendListEntry = {
  contact: Contact;
  sendEmail: string;
};

type Persisted = {
  sendListOpen: boolean;
  items: ZendContactenListItem[];
};

function normalizeListItem(item: ZendContactenListItem): ZendContactenListItem {
  if (typeof item.sendEmail === "string") return item;
  return {
    ...item,
    sendEmail: contactPreferredEmail(item.contact) ?? "",
  };
}

function loadPersisted(): Persisted | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Persisted;
    if (!parsed || !Array.isArray(parsed.items)) return null;
    return { ...parsed, items: parsed.items.map(normalizeListItem) };
  } catch {
    return null;
  }
}

function savePersisted(data: Persisted) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

function clearPersisted() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export type ZendContactenContextValue = {
  canAdHocEmail: boolean;
  canZendContacten: boolean;
  sendListOpen: boolean;
  sendList: Contact[];
  sendListEntries: ZendContactenSendListEntry[];
  items: ZendContactenListItem[];
  addToSendList: (contact: Contact, sourceApiPath: string) => void;
  removeFromSendList: (id: number) => void;
  sendModalOpen: boolean;
  setSendModalOpen: (v: boolean) => void;
  sendTo: string;
  setSendTo: (v: string) => void;
  sendBody: string;
  setSendBody: (v: string) => void;
  sendBusy: boolean;
  sendErr: string;
  submitSendVcards: () => Promise<void>;
  toggleSendMode: () => void;
  clearSendList: () => void;
  clearSendErr: () => void;
};

const ZendContactenContext = createContext<ZendContactenContextValue | null>(null);

export function ZendContactenProvider({
  user,
  children,
}: {
  user: User | null;
  children: ReactNode;
}) {
  const initial = useMemo(() => loadPersisted(), []);
  const [items, setItems] = useState<ZendContactenListItem[]>(() => initial?.items ?? []);
  const [sendListOpen, setSendListOpen] = useState(() => initial?.sendListOpen ?? false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendBody, setSendBody] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [sendErr, setSendErr] = useState("");
  const [emailPick, setEmailPick] = useState<{
    contact: Contact;
    sourceApiPath: string;
    options: ContactEmailOption[];
  } | null>(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const canAdHocEmail =
    !!user && (!!user.is_super_admin || user.app_role === "secretaris" || user.app_role === "beheerder");
  const canZendContacten = !!user;

  useEffect(() => {
    if (!user) {
      setItems([]);
      setSendListOpen(false);
      setSendModalOpen(false);
      clearPersisted();
      return;
    }
    savePersisted({ sendListOpen, items });
  }, [user, sendListOpen, items]);

  const commitAddToSendList = useCallback(
    (contact: Contact, sourceApiPath: string, sendEmail: string) => {
      setItems((prev) => {
        if (prev.some((x) => x.contact.id === contact.id)) return prev;
        const path = sourceApiPath || contactListApiPath(contact.entity_kind);
        return [...prev, { contact, sourceApiPath: path, sendEmail }];
      });
    },
    []
  );

  const addToSendList = useCallback(
    (contact: Contact, sourceApiPath: string) => {
      if (itemsRef.current.some((x) => x.contact.id === contact.id)) return;

      const options = contactEmailOptions(contact);
      if (options.length === 0) {
        window.alert(
          `${contact.first_name} ${contact.last_name}`.trim() +
            " heeft geen e-mailadres op de kaart. De persoon wordt wel aan de lijst toegevoegd (voor vCards)."
        );
        commitAddToSendList(contact, sourceApiPath, "");
        return;
      }
      if (options.length === 1) {
        commitAddToSendList(contact, sourceApiPath, options[0].email);
        return;
      }
      setEmailPick({ contact, sourceApiPath, options });
    },
    [commitAddToSendList]
  );

  const removeFromSendList = useCallback((id: number) => {
    setItems((prev) => prev.filter((x) => x.contact.id !== id));
  }, []);

  const toggleSendMode = useCallback(() => {
    setSendListOpen((wasOpen) => {
      if (wasOpen) setSendModalOpen(false);
      return !wasOpen;
    });
  }, []);

  const clearSendList = useCallback(() => {
    setItems([]);
    setSendModalOpen(false);
  }, []);

  const submitSendVcards = useCallback(async () => {
    setSendErr("");
    if (!sendTo.trim()) {
      setSendErr("Vul het e-mailadres van de ontvanger in.");
      return;
    }
    if (items.length === 0) {
      setSendErr("Voeg minstens één contact toe aan de lijst.");
      return;
    }
    setSendBusy(true);
    try {
      const byPath = new Map<string, number[]>();
      for (const { contact, sourceApiPath } of items) {
        const path = sourceApiPath || contactListApiPath(contact.entity_kind);
        const list = byPath.get(path) ?? [];
        list.push(contact.id);
        byPath.set(path, list);
      }

      if (byPath.size === 1) {
        const [path, contact_ids] = [...byPath.entries()][0];
        await api.post(`${path}/send-vcard-email`, {
          to_email: sendTo.trim(),
          body_text: sendBody.trim() || undefined,
          contact_ids,
        });
      } else {
        const contact_ids = items.map((x) => x.contact.id);
        await api.post(`${ALLE_CONTACTEN_API}/send-vcard-email`, {
          to_email: sendTo.trim(),
          body_text: sendBody.trim() || undefined,
          contact_ids,
        });
      }

      setSendModalOpen(false);
      setSendTo("");
      setSendBody("");
      setItems([]);
      setSendListOpen(false);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { error?: string } } };
      setSendErr(ex.response?.data?.error ?? "Versturen mislukt");
    } finally {
      setSendBusy(false);
    }
  }, [items, sendBody, sendTo]);

  const sendList = useMemo(() => items.map((x) => x.contact), [items]);
  const sendListEntries = useMemo(
    (): ZendContactenSendListEntry[] =>
      items.map(({ contact, sendEmail }) => ({ contact, sendEmail })),
    [items]
  );

  const value = useMemo(
    (): ZendContactenContextValue => ({
      canAdHocEmail,
      canZendContacten,
      sendListOpen,
      sendList,
      sendListEntries,
      items,
      addToSendList,
      removeFromSendList,
      sendModalOpen,
      setSendModalOpen,
      sendTo,
      setSendTo,
      sendBody,
      setSendBody,
      sendBusy,
      sendErr,
      submitSendVcards,
      toggleSendMode,
      clearSendList,
      clearSendErr: () => setSendErr(""),
    }),
    [
      canAdHocEmail,
      canZendContacten,
      sendListOpen,
      sendList,
      sendListEntries,
      items,
      addToSendList,
      removeFromSendList,
      sendModalOpen,
      sendTo,
      sendBody,
      sendBusy,
      sendErr,
      submitSendVcards,
      toggleSendMode,
      clearSendList,
    ]
  );

  return (
    <ZendContactenContext.Provider value={value}>
      {children}
      {emailPick && (
        <PickContactEmailModal
          contact={emailPick.contact}
          options={emailPick.options}
          onConfirm={(email) => {
            commitAddToSendList(emailPick.contact, emailPick.sourceApiPath, email);
            setEmailPick(null);
          }}
          onCancel={() => setEmailPick(null)}
        />
      )}
    </ZendContactenContext.Provider>
  );
}

export function useZendContactenContext(): ZendContactenContextValue {
  const ctx = useContext(ZendContactenContext);
  if (!ctx) {
    throw new Error("useZendContactenContext must be used within ZendContactenProvider");
  }
  return ctx;
}

/** Per lijstpagina: zelfde globale selectie, met api-pad voor vCard-verzending. */
export function useVcardEmailSend(sourceApiPath: string) {
  const ctx = useZendContactenContext();
  return useMemo(
    () => ({
      ...ctx,
      addToSendList: (contact: Contact) => ctx.addToSendList(contact, sourceApiPath),
    }),
    [ctx, sourceApiPath]
  );
}
